import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../http-client';

/**
 * Batched per-event draw status powering the Manage Draws landing page.
 *
 * One round-trip for all events under all loaded rodeos — replaces the
 * old N+1 `/event-registrations/count?event_id=...` loop and also gives
 * us the placement_status, last-action timestamps, and drawn-out count
 * the admin needs to triage from the list.
 *
 * Lives in its own extension file (not `useDrawOps.ts`) so it can ship
 * without touching the file a parallel draw-reset session is editing.
 */

export type DrawPlacementStatus =
  | 'none'
  | 'draft'
  | 'published'
  | 'finalized';

export interface EventDrawStatus {
  event_id: string;
  execution_id: string | null;
  placement_status: DrawPlacementStatus;
  executed_at: string | null;
  published_at: string | null;
  finalized_at: string | null;
  /** Active (non-cancelled) registrations. */
  entered_count: number;
  /** Registrations cancelled (e.g. refunded at publish). */
  cancelled_count: number;
  /** Placed draft entries (have a performance slot). */
  placed_count: number;
  /**
   * Active registrations whose draft entry is unplaced — capacity
   * exceeded, draw-out preference, or otherwise needing manual attention.
   * Combines what the detail page splits across "Draw outs" + "Not drawn"
   * because both buckets require the same admin follow-up.
   */
  not_drawn_count: number;
  unpaid_count: number;
  has_capacity: boolean;
  /**
   * Last time the per-event notifications-sent batch ran for the most
   * recent execution. `null` when no notifications have been sent yet.
   * Powers the "Notifications sent" badge on the Manage Draws list and
   * may be older than the saved org template's `last_sent_at` if the
   * latest execution was reset and re-run.
   */
  notifications_sent_at: string | null;
  notifications_sent_count: number | null;
  /**
   * True when the public draw-results toggle (events.draw_results_visible)
   * is on. Powers the "Live / showing to contestants" badge — the
   * unfinalize path force-clears this column so it tracks current state.
   */
  results_visible: boolean;
}

interface EventStatusesResponse {
  statuses: Record<string, EventDrawStatus>;
  success: boolean;
}

export const drawListStatusKeys = {
  all: ['draw-list-status'] as const,
  batch: (eventIds: string[]) =>
    [...drawListStatusKeys.all, 'batch', [...eventIds].sort().join(',')] as const,
};

export function useEventDrawStatuses(eventIds: string[]) {
  // Sort + join inside the key factory so the cache key is order-stable
  // even when callers pass the same set of ids in a different order
  // between renders.
  return useQuery({
    queryKey: drawListStatusKeys.batch(eventIds),
    queryFn: async () => {
      if (eventIds.length === 0) {
        return { statuses: {}, success: true } as EventStatusesResponse;
      }
      const qs = new URLSearchParams();
      qs.set('event_ids', eventIds.join(','));
      return httpClient.get<EventStatusesResponse>(
        `/draw-ops/event-statuses?${qs.toString()}`,
      );
    },
    enabled: eventIds.length > 0,
    staleTime: 15_000,
  });
}
